// Salesperson Redux Slice

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { salespersonApi } from '../../api/salespersonApi';

// ─── Async Thunks ──────────────────────────────────────────────────────────────

export const fetchSalespersons = createAsyncThunk(
  'salespersons/fetchAll',
  // params: { period } or { from, to } — narrows every NUMBER on the screen
  // (inquiries, turnover, sold/lost, balance); the people stay listed.
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await salespersonApi.getList(params);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load salespersons');
    }
  }
);

export const fetchDeletedSalespersons = createAsyncThunk(
  'salespersons/fetchDeleted',
  async (_, { rejectWithValue }) => {
    try {
      const res = await salespersonApi.getList({ include_deleted: true });
      const all = res.data.results ?? res.data;
      return all.filter((s) => s.is_deleted);
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to load deleted salespersons');
    }
  }
);

export const createSalesperson = createAsyncThunk(
  'salespersons/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await salespersonApi.create(data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create salesperson');
    }
  }
);

export const updateSalesperson = createAsyncThunk(
  'salespersons/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await salespersonApi.update(id, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update salesperson');
    }
  }
);

export const toggleSalespersonActive = createAsyncThunk(
  'salespersons/toggleActive',
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      const res = await salespersonApi.toggleActive(id, is_active);
      return res.data.salesperson ?? res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update status');
    }
  }
);

export const deleteSalesperson = createAsyncThunk(
  'salespersons/delete',
  async ({ id, password }, { rejectWithValue }) => {
    try {
      await salespersonApi.remove(id, password);
      return id;
    } catch (err) {
      const data = err.response?.data;
      const msg = Array.isArray(data)
        ? data[0]
        : (data?.detail || data?.password || 'Failed to delete salesperson');
      return rejectWithValue(msg);
    }
  }
);

export const restoreSalesperson = createAsyncThunk(
  'salespersons/restore',
  async (id, { rejectWithValue }) => {
    try {
      const res = await salespersonApi.restore(id);
      return { id, salesperson: res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to restore salesperson');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const salespersonSlice = createSlice({
  name: 'salespersons',
  initialState: {
    list: [],
    deletedList: [],
    count: 0,
    deletedCount: 0,
    teamTurnover: '0.00',   // total business the whole team brought in
    teamDeals: 0,
    teamInquiries: 0,
    teamLost: 0,
    teamBalance: '0.00',    // still to collect on advance deals
    loading: false,
    loadingDeleted: false,
    deletedFetched: false,
    submitting: false,
    error: null,
  },
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    // fetchSalespersons
    builder
      .addCase(fetchSalespersons.pending,    (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSalespersons.fulfilled,  (state, { payload }) => {
        state.loading = false;
        state.list    = payload.results ?? payload;
        state.count   = payload.count   ?? (payload.length || 0);
        state.teamTurnover = payload.team_turnover ?? '0.00';
        state.teamDeals    = payload.team_deals ?? 0;
        state.teamInquiries = payload.team_inquiries ?? 0;
        state.teamLost      = payload.team_lost ?? 0;
        state.teamBalance   = payload.team_balance ?? '0.00';
      })
      .addCase(fetchSalespersons.rejected,   (state, { payload }) => { state.loading = false; state.error = payload; });

    // fetchDeletedSalespersons
    builder
      .addCase(fetchDeletedSalespersons.pending,   (state) => { state.loadingDeleted = true; state.error = null; })
      .addCase(fetchDeletedSalespersons.fulfilled, (state, { payload }) => {
        state.loadingDeleted  = false;
        state.deletedList     = payload;
        state.deletedCount    = payload.length;
        state.deletedFetched  = true;
      })
      .addCase(fetchDeletedSalespersons.rejected,  (state, { payload }) => { state.loadingDeleted = false; state.error = payload; });

    // createSalesperson
    builder
      .addCase(createSalesperson.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(createSalesperson.fulfilled, (state, { payload }) => {
        state.submitting = false;
        state.list.unshift(payload);
        state.count += 1;
      })
      .addCase(createSalesperson.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });

    // updateSalesperson
    builder
      .addCase(updateSalesperson.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(updateSalesperson.fulfilled, (state, { payload }) => {
        state.submitting = false;
        const idx = state.list.findIndex((s) => s.id === payload.id);
        if (idx !== -1) state.list[idx] = payload;
      })
      .addCase(updateSalesperson.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });

    // toggleSalespersonActive (optimistic)
    builder
      .addCase(toggleSalespersonActive.pending, (state, { meta }) => {
        const idx = state.list.findIndex((s) => s.id === meta.arg.id);
        if (idx !== -1) state.list[idx].is_active = meta.arg.is_active;
      })
      .addCase(toggleSalespersonActive.fulfilled, (state, { payload }) => {
        const idx = state.list.findIndex((s) => s.id === payload.id);
        if (idx !== -1) state.list[idx] = payload;
      })
      .addCase(toggleSalespersonActive.rejected, (state, { payload, meta }) => {
        const idx = state.list.findIndex((s) => s.id === meta.arg.id);
        if (idx !== -1) state.list[idx].is_active = !meta.arg.is_active;
        state.error = payload;
      });

    // deleteSalesperson — soft delete, moves to deletedList
    builder
      .addCase(deleteSalesperson.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(deleteSalesperson.fulfilled, (state, { payload: id }) => {
        state.submitting = false;
        const removed = state.list.find((s) => s.id === id);
        state.list     = state.list.filter((s) => s.id !== id);
        state.count    = Math.max(0, state.count - 1);
        if (removed && state.deletedFetched) {
          state.deletedList.unshift({ ...removed, is_deleted: true });
          state.deletedCount += 1;
        }
      })
      .addCase(deleteSalesperson.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });

    // restoreSalesperson
    builder
      .addCase(restoreSalesperson.pending,   (state) => { state.submitting = true; state.error = null; })
      .addCase(restoreSalesperson.fulfilled, (state, { payload: { id, salesperson } }) => {
        state.submitting  = false;
        state.deletedList = state.deletedList.filter((s) => s.id !== id);
        state.deletedCount = Math.max(0, state.deletedCount - 1);
        state.list.unshift({ ...salesperson, is_active: true });
        state.count += 1;
      })
      .addCase(restoreSalesperson.rejected,  (state, { payload }) => { state.submitting = false; state.error = payload; });
  },
});

export const { clearError } = salespersonSlice.actions;
export default salespersonSlice.reducer;
